import { useCallback, useEffect, useState } from 'react'
import { Button, Checkbox, Col, Flex, Form, Row, Skeleton } from 'antd'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import BankInfo from '../components/BankInfo'
import SelectClient from '../components/SelectClient'
import ActionConfirmationModal from 'src/app/shared/components/modal/action-confirmation-modal'
import { DangerIcon } from 'src/assets/icons'
import { formatDataBeforeSubmit, generateValidationSchema, mappingDataToForm } from '../components/utils'
import { IBankUserSetup, IGetListRole } from 'src/app/shared/models/bankUserSetup.model'
import { getBankUserDetail, updateBankUser } from 'src/app/shared/services/BankUserService'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { SERVER_STATUS_CODES } from 'src/app/shared/constants/serverResponseStatusCodes.constants'
import '../components/index.scss'
import { STATUS_CODES, priGrpTypCodeName } from 'src/app/shared/constants/common.constants'
import { getUserRoleListByType } from 'src/app/shared/services/ClientUserSetupService'
import SelectCpn from '../components/SelectCpn'

const BankSetupEdit = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const { id } = params
  const [isLoading, setIsLoading] = useState(false)
  const [usernameConfirm, setUsernameConfirm] = useState('')
  const [isFetched, setIsFetched] = useState(false)
  const [checkLocked, setCheckLocked] = useState<boolean>()
  const [checkArchive, setCheckArchive] = useState<boolean>()
  const [payloadForm, setPayloadForm] = useState<any>({})
  const [descriptionModal, setDescriptionModal] = useState('')
  const [checkStatus, setCheckStatus] = useState(0)
  const [oldStatus, setOldStatus] = useState(0)
  const [isLoadingRole, setIsLoadingRole] = useState<boolean>(false)
  const [roleList, setRoleList] = useState<IGetListRole.Response>([])
  const [isModalConfirmOpened, setIsModalConfirmOpened] = useState(false)
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    getValues,
    setError,
    setValue,
    trigger,
    clearErrors,
    resetField,
  } = useForm<any>({
    mode: 'all',
    defaultValues: async () => await getBankInfo(),
    resolver: yupResolver(generateValidationSchema(t)),
  })
  const locked = useWatch({
    control,
    name: 'isLocked',
  })
  const archive = useWatch({
    control,
    name: 'isArchive',
  })
  const getBankInfo = useCallback(async () => {
    if (id) {
      const res = await getBankUserDetail(id)

      const formattedData = mappingDataToForm(res?.data)
      setIsFetched(true)
      if (formattedData?.status === 2) {
        setCheckArchive(true)
      }
      if (formattedData?.status === 3) {
        setCheckLocked(true)
      }
      if (formattedData?.status === 4) {
        setCheckArchive(true)
        setCheckLocked(true)
      }
      setPayloadForm(formattedData)
      setOldStatus(formattedData?.status)
      setUsernameConfirm(formattedData?.username)
      return formattedData
    }
    navigate(menuUrls.bankUserSetup)
  }, [id, navigate])

  useEffect(() => {
    setValue('isArchive', checkArchive)
    setValue('isLocked', checkLocked)
  }, [checkArchive, checkLocked, setValue])

  useEffect(() => {
    if (!locked && !archive) {
      setCheckStatus(1)
    }
    if (!locked && archive) {
      setCheckStatus(2)
    }
    if (locked && !archive) {
      setCheckStatus(3)
    }
    if (locked && archive) {
      setCheckStatus(4)
    }
    setValue('status', checkStatus)
  }, [archive, checkStatus, locked, setValue])

  const editBankUser = async (data: IBankUserSetup) => {
    setIsLoading(true)
    const res = await updateBankUser(formatDataBeforeSubmit(data), id)
    const { statusCode } = res
    setIsLoading(false)

    if (statusCode === 'Success') {
      // Duplicate found and check lock and inactive
      if (!res?.data?.success) {
        switch (res?.data?.code) {
          case SERVER_STATUS_CODES.DUP03_CHECK_STATUS_DUPLICATED:
            setError('email', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_EMAIL) })
            break
          case SERVER_STATUS_CODES.DUP04_CHECK_STATUS_DUPLICATED:
            setError('email', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_EMAIL) })
            break
          case SERVER_STATUS_CODES.DUP05_CHECK_STATUS_DUPLICATED:
            setError('email', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_EMAIL) })
            break
          case SERVER_STATUS_CODES.DUP06_CHECK_STATUS_DUPLICATED:
            setError('email', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_EMAIL) })
            break
          default:
            break
        }
      }

      // No Duplicate
      if (res?.data?.success) {
        let message = ''
        if (res?.data?.code === 'Active') {
          message = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.USER_ACTIVE, {
            Username: data?.username,
          })
        } else {
          message = t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BANK_USER_UPDATED_SUCCESSFULLY, {
            Username: data?.username,
          })
        }

        navigate(menuUrls.bankUserSetup, {
          state: {
            bankUserSetup: {
              type: 'success',
              message: message,
            },
          },
        })
      }
    }
  }

  const onSubmit = (data: IBankUserSetup) => {
    // check status bankUser
    const convertedData = {
      ...data,
      role: data?.role,
    }
    // case 2 -> 1, 4 -> 1( show modal make Active Bank User)
    if ((checkStatus === 1 && oldStatus === 2) || (checkStatus === 1 && oldStatus === 4)) {
      setDescriptionModal(
        t('BankSetup:editBankUser.modal.description.descriptionActive', {
          Username: `${usernameConfirm}`,
        }),
      )
      setIsModalConfirmOpened(true)

      setPayloadForm(convertedData)
      return
    }
    //case 3 -> 1( show modal make Unlock Bank User)
    if (checkStatus === 1 && oldStatus === 3) {
      setDescriptionModal(
        t('BankSetup:editBankUser.modal.description.descriptionUnlock', {
          Username: `${usernameConfirm}`,
        }),
      )
      setIsModalConfirmOpened(true)

      setPayloadForm(convertedData)
      return
    }

    // if don't make Active or Unlock Bank User
    editBankUser(convertedData)
  }

  const onClose = () => {
    setIsModalConfirmOpened(false)
  }

  const handleOnSubmitModal = (data: IBankUserSetup) => {
    const convertedData = {
      ...data,
      role: data?.role,
    }
    editBankUser(convertedData)
  }
  const handleGetRoleList = useCallback(async () => {
    try {
      setIsLoadingRole(true)
      const listResponse = await getUserRoleListByType(priGrpTypCodeName.bankAccess)
      if (listResponse.statusCode === STATUS_CODES?.SUCCESS) {
        setRoleList(listResponse.data)
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setIsLoadingRole(false)
    }
  }, [])

  useEffect(() => {
    handleGetRoleList()
  }, [handleGetRoleList])

  const isSaveButtonDisabled =
    !!Object.keys(errors)?.length ||
    !Object.keys(dirtyFields)?.length ||
    !getValues('firstName')?.trim() ||
    !getValues('lastName')?.trim() ||
    !getValues('email')?.trim() ||
    (!getValues('role')?.trim())

  const handleCancel = () => {
    navigate(menuUrls.bankUserSetup)
  }
  return (
    <Form
      name='bank-user-setup-form'
      className='form-bank-user-container'
      layout='vertical'
      onFinish={handleSubmit(onSubmit)}
    >
      {isFetched ? (
        <>
          <BankInfo
            control={control}
            errors={errors}
            getValues={getValues}
            isDisable={true}
            setValue={setValue}
            trigger={trigger}
          />

          <SelectCpn
            control={control}
            errors={errors}
            title={t('BankSetup:addNewBankUser.label.selectApplicableRoles')}
            placeholder={t('BankSetup:addNewBankUser.placeholder.selectApplicableRoles')}
            options={roleList.map((item) => ({
              label: item.name,
              value: item.id,
            }))}
            selectName='role'
            defaultSelectedValue={''}
            clearErrors={clearErrors}
            trigger={trigger}
            setValue={setValue}
            onSelectBlur={() => {
              trigger('role')
            }}
            loading={isLoadingRole}
          />

          <SelectClient
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            resetField={resetField}
            setValue={setValue}
            includesArchived={false}
          />

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item valuePropName='checked'>
                <Controller
                  name='isLocked'
                  control={control}
                  render={({ field }) => (
                    <Checkbox {...field} checked={field.value}>
                      {t('BankSetup:addNewBankUser.label.userLocked')}
                    </Checkbox>
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item valuePropName='checked'>
                <Controller
                  name='isArchive'
                  control={control}
                  render={({ field }) => (
                    <Checkbox {...field} checked={field.value}>
                      {t('BankSetup:addNewBankUser.label.archiveUser')}
                    </Checkbox>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex className='button-group' justify='flex-end' gap={24}>
            <Button className='cancel-button' onClick={handleCancel}>
              {t('OverallFunctionality:cancel_btn')}
            </Button>

            <Button
              className='save-button'
              type='primary'
              disabled={isSaveButtonDisabled}
              htmlType='button'
              onClick={handleSubmit(onSubmit)}
              loading={isLoading}
            >
              {t('OverallFunctionality:save_changes_btn')}
            </Button>
          </Flex>
          <ActionConfirmationModal
            icon={<DangerIcon />}
            title={t('BankSetup:editBankUser.modal.title.confirmation')}
            footer={[
              <Button size='middle' className='cancel-button' onClick={onClose}>
                {t('common:no')}
              </Button>,
              <Button
                type='primary'
                onClick={() => {
                  handleOnSubmitModal(payloadForm)
                }}
                loading={isLoading}
              >
                {t('common:yes')}
              </Button>,
            ]}
            descriptionType='string'
            onClose={onClose}
            description={<RichTextField richText={descriptionModal || ''} />}
            isOpen={isModalConfirmOpened}
          />
        </>
      ) : (
        <Skeleton />
      )}
    </Form>
  )
}

export default BankSetupEdit
